import type { CSSProperties, ReactNode } from 'react';

import type { StorefrontPostSummary } from '@/features/storefront/api/storefront';
import { StorefrontContainer as Container } from '@/features/storefront/components/StorefrontContainer';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { focalStyle } from '@/features/storefront/focal';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import {
  EMPTY_TEASER,
  postCoverUrl,
  teaserIndexPath,
  teaserPosts,
  type BlogTeaser,
  type TeaserPost,
} from '@/features/storefront/sections/blogTeaser';
import type { BlogPostsSection } from '@/features/storefront/sections/schema';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/i18n/format';

/**
 * Blog posts as cards, rows, a lead story and a sidebar list, or a lead story
 * beside a grid of picture cards.
 *
 * Shared by the `blog_posts` section and the blog's own index, so a shop's
 * teaser on the home page and the page it leads to are drawn from the same
 * pieces. Everything visual reads the theme's custom properties, so the posts
 * look like the shop rather than like a blog widget pasted into it.
 */

const POST_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };

export type PostCardVariant = 'card' | 'row' | 'lead' | 'compact';

export interface PostCardProps {
  post: StorefrontPostSummary;
  variant: PostCardVariant;
  /** Wraps the card in its link - routed on the shop, inert in the customiser. */
  link: (className: string, children: ReactNode) => ReactNode;
  showImage?: boolean;
  showExcerpt?: boolean;
  showDate?: boolean;
  /** `h2` on the blog's own page, `h3` under a section's heading. */
  headingLevel?: 'h2' | 'h3';
}

const FRAME: Record<PostCardVariant, string> = {
  card: 'mb-3 aspect-[16/10] w-full',
  lead: 'mb-4 aspect-[16/9] w-full',
  row: 'aspect-[4/3] w-32 shrink-0 sm:w-56',
  compact: 'aspect-square w-20 shrink-0',
};

const TITLE: Record<PostCardVariant, string> = {
  card: 'text-lg',
  lead: 'text-2xl sm:text-3xl',
  row: 'text-lg sm:text-xl',
  compact: 'text-base',
};

const EXCERPT_LINES: Record<PostCardVariant, string> = {
  card: 'line-clamp-3',
  lead: 'line-clamp-4',
  row: 'line-clamp-2 sm:line-clamp-3',
  compact: 'hidden',
};

function Cover({ post, variant }: { post: StorefrontPostSummary; variant: PostCardVariant }) {
  const frame = cn('overflow-hidden', FRAME[variant]);
  const style: CSSProperties = { borderRadius: 'var(--storefront-radius)' };
  const cover = postCoverUrl(post);
  if (!cover) {
    return <div className={frame} style={{ ...style, backgroundColor: 'var(--storefront-surface)' }} />;
  }
  return (
    // The frame clips, so a zoomed cover stays inside its card.
    <div className={frame} style={style}>
      <StorefrontImage
        url={cover}
        sizes={variant === 'lead' ? '(min-width: 1024px) 60vw, 100vw' : '(min-width: 1024px) 33vw, 100vw'}
        alt=""
        className="size-full object-cover"
        style={focalStyle(post.image_focal)}
      />
    </div>
  );
}

export function PostCard({
  post,
  variant,
  link,
  showImage = true,
  showExcerpt = true,
  showDate = true,
  headingLevel = 'h3',
}: PostCardProps) {
  const { locale } = useStorefrontLanguage();
  const Heading = headingLevel;
  const horizontal = variant === 'row' || variant === 'compact';
  const date = showDate && post.published_at ? formatDate(post.published_at, locale, POST_DATE) : '';

  const words = (
    <div className="min-w-0">
      {date ? (
        <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--storefront-muted)' }}>
          {date}
        </p>
      ) : null}
      <Heading className={cn('mt-1 group-hover:underline', TITLE[variant])} style={STOREFRONT_HEADING_STYLE}>
        {post.title}
      </Heading>
      {showExcerpt && post.excerpt ? (
        <p className={cn('mt-1.5 text-sm', EXCERPT_LINES[variant])} style={{ color: 'var(--storefront-muted)' }}>
          {post.excerpt}
        </p>
      ) : null}
    </div>
  );

  return (
    <article data-post-variant={variant}>
      {link(
        cn('group block', horizontal && 'flex items-start gap-4 sm:gap-5'),
        <>
          {showImage ? <Cover post={post} variant={variant} /> : null}
          {words}
        </>,
      )}
    </article>
  );
}

/**
 * A story drawn as its picture, with the date, title and summary over a dark
 * gradient at its foot - the `lead_grid` layout's cards. `lead` is the large
 * one; a `tile` is one of the grid beside it, and keeps only its title.
 *
 * Without a picture (none on the post, or pictures switched off) the card is
 * the surface colour and the words stand on it in the theme's own colours.
 */
export function OverlayPostCard({
  post,
  size,
  link,
  showImage = true,
  showExcerpt = true,
  showDate = true,
}: Omit<PostCardProps, 'variant' | 'headingLevel'> & { size: 'lead' | 'tile' }) {
  const { locale } = useStorefrontLanguage();
  const lead = size === 'lead';
  const picture = (showImage && postCoverUrl(post)) || '';
  const date = showDate && post.published_at ? formatDate(post.published_at, locale, POST_DATE) : '';
  const words = picture ? { color: '#fff' } : undefined;

  return (
    <article data-post-variant={lead ? 'overlay_lead' : 'overlay_tile'} className="h-full">
      {link(
        cn(
          'group relative flex h-full flex-col justify-end overflow-hidden',
          lead
            ? 'aspect-[4/3] md:aspect-auto md:min-h-[26rem]'
            : 'aspect-square md:aspect-auto md:min-h-[12rem]',
        ),
        <>
          {picture ? (
            <>
              <StorefrontImage
                url={picture}
                sizes={lead ? '(min-width: 768px) 50vw, 100vw' : '(min-width: 768px) 25vw, 50vw'}
                alt=""
                className="absolute inset-0 size-full object-cover transition-transform group-hover:scale-[1.03]"
                style={focalStyle(post.image_focal)}
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent"
              />
            </>
          ) : null}
          <div className={cn('relative min-w-0', lead ? 'p-5 sm:p-7' : 'p-3 sm:p-4')} style={words}>
            {date ? (
              <p
                className="text-xs uppercase tracking-wide"
                style={{ color: picture ? 'rgba(255,255,255,0.8)' : 'var(--storefront-muted)' }}
              >
                {date}
              </p>
            ) : null}
            <h3
              className={cn(
                'mt-1 group-hover:underline',
                lead ? 'text-2xl sm:text-3xl' : 'line-clamp-3 text-sm sm:text-base',
              )}
              style={STOREFRONT_HEADING_STYLE}
            >
              {post.title}
            </h3>
            {lead && showExcerpt && post.excerpt ? (
              <p
                className="mt-2 line-clamp-2 text-sm sm:line-clamp-3"
                style={{ color: picture ? 'rgba(255,255,255,0.85)' : 'var(--storefront-muted)' }}
              >
                {post.excerpt}
              </p>
            ) : null}
          </div>
        </>,
      )}
    </article>
  );
}

/** An overlay card's own frame: rounded, and the surface colour under a missing picture. */
const OVERLAY_FRAME: CSSProperties = {
  borderRadius: 'var(--storefront-radius)',
  backgroundColor: 'var(--storefront-surface)',
};

/**
 * One large story beside a two-by-two grid of the next four; stacked under it
 * on a phone. Fewer posts fill what there is rather than leave holes: a lone
 * story takes the width, and one, two or three beside the lead share the
 * grid's height between them.
 */
function LeadGrid({
  posts,
  card,
}: {
  posts: TeaserPost[];
  card: (post: TeaserPost, size: 'lead' | 'tile') => ReactNode;
}) {
  const [lead, ...others] = posts;
  const rest = others.slice(0, 4);
  if (rest.length === 0) return <div className="h-full">{card(lead, 'lead')}</div>;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {card(lead, 'lead')}
      <ul
        data-blog-lead-grid={rest.length}
        className={cn(
          'grid gap-4',
          // Four: two by two. Three: the last runs across both columns. Two:
          // side by side on a phone, one above the other beside the lead.
          rest.length >= 3 && 'grid-cols-2 md:grid-rows-2',
          rest.length === 3 && '[&>li:last-child]:col-span-2',
          rest.length === 2 && 'grid-cols-2 md:grid-cols-1 md:grid-rows-2',
        )}
      >
        {rest.map((post) => (
          <li key={`${post.blogHandle}/${post.handle}`}>{card(post, 'tile')}</li>
        ))}
      </ul>
    </div>
  );
}

/** A divided column of horizontal rows. */
export function PostRows({ children }: { children: ReactNode }) {
  return (
    <ul
      className="divide-y [&>li]:py-5 first:[&>li]:pt-0 last:[&>li]:pb-0"
      style={{ borderColor: 'color-mix(in srgb, currentColor 12%, transparent)' }}
    >
      {children}
    </ul>
  );
}

/** How many cards stand in a row, for the number of posts there are. */
function gridColumns(count: number): string {
  if (count <= 2) return 'sm:grid-cols-2';
  if (count === 4) return 'sm:grid-cols-2 lg:grid-cols-4';
  return 'sm:grid-cols-2 lg:grid-cols-3';
}

export interface BlogPostsViewProps {
  section: BlogPostsSection;
  /** The customiser: says why the section is empty instead of vanishing. */
  preview: boolean;
  teaser?: BlogTeaser;
  heading: ReactNode;
  /** A link that is inert in the customiser and routed on the shop. */
  link: (href: string, className: string, style: CSSProperties | undefined, children: ReactNode) => ReactNode;
  /** The CTA's words, typeable in the customiser. */
  ctaText: (value: string) => ReactNode;
}

function Hint({ children }: { children: ReactNode }) {
  return (
    <p
      className="rounded-xl border border-dashed px-4 py-6 text-center text-sm"
      style={{ borderColor: 'var(--storefront-muted)', color: 'var(--storefront-muted)' }}
    >
      {children}
    </p>
  );
}

export function BlogPostsView({ section, preview, teaser = EMPTY_TEASER, heading, link, ctaText }: BlogPostsViewProps) {
  const features = useStorefrontFeatures();
  const posts = features.blog ? teaserPosts(teaser, section.blog, section.limit) : [];

  if (posts.length === 0) {
    if (!preview) return null;
    return (
      <Container>
        {heading}
        <Hint>
          {features.blog
            ? 'Your newest blog posts appear here once you publish one. Until then this section is hidden on your shop.'
            : 'Your blog is switched off, so this section is hidden on your shop.'}
        </Hint>
      </Container>
    );
  }

  const postHref = (post: TeaserPost) =>
    `/blogs/${encodeURIComponent(post.blogHandle)}/${encodeURIComponent(post.handle)}`;

  const card = (post: TeaserPost, variant: PostCardVariant) => (
    <PostCard
      post={post}
      variant={variant}
      showImage={section.show_image}
      showExcerpt={section.show_excerpt}
      showDate={section.show_date}
      link={(className, children) => link(postHref(post), className, undefined, children)}
    />
  );

  let body: ReactNode;
  if (section.layout === 'lead_grid') {
    body = (
      <LeadGrid
        posts={posts}
        card={(post, size) => (
          <OverlayPostCard
            post={post}
            size={size}
            showImage={section.show_image}
            showExcerpt={section.show_excerpt}
            showDate={section.show_date}
            link={(className, children) => link(postHref(post), className, OVERLAY_FRAME, children)}
          />
        )}
      />
    );
  } else if (section.layout === 'list') {
    body = (
      <PostRows>
        {posts.map((post) => (
          <li key={`${post.blogHandle}/${post.handle}`}>{card(post, 'row')}</li>
        ))}
      </PostRows>
    );
  } else if (section.layout === 'featured') {
    const [lead, ...rest] = posts;
    body = (
      <div className="grid gap-8 md:grid-cols-5">
        <div className="md:col-span-3">{card(lead, 'lead')}</div>
        {rest.length > 0 ? (
          <div className="md:col-span-2">
            <PostRows>
              {rest.map((post) => (
                <li key={`${post.blogHandle}/${post.handle}`}>{card(post, 'compact')}</li>
              ))}
            </PostRows>
          </div>
        ) : null}
      </div>
    );
  } else {
    body = (
      <ul className={cn('grid gap-8', gridColumns(posts.length))}>
        {posts.map((post) => (
          <li key={`${post.blogHandle}/${post.handle}`}>{card(post, 'card')}</li>
        ))}
      </ul>
    );
  }

  const index = teaserIndexPath(teaser, section.blog);

  return (
    <Container>
      {heading}
      <div data-blog-layout={section.layout}>{body}</div>
      {section.cta_label && index ? (
        <div className="mt-10 text-center">
          {link(
            index,
            'inline-flex h-11 items-center px-6 text-sm font-semibold',
            STOREFRONT_BUTTON_STYLE,
            ctaText(section.cta_label),
          )}
        </div>
      ) : null}
    </Container>
  );
}
